import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import InvoicePayModal from "@/components/InvoicePayModal";
import CreditMemosCard from "@/components/CreditMemosCard";
import PdfPreview from "@/components/PdfPreview";
import CustomerInvoiceAttachments from "@/components/CustomerInvoiceAttachments";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
    ArrowLeft, FileText, Download, CreditCard, AlertTriangle, Lock,
    Clock, CheckCircle2, RotateCw, Loader2,
} from "lucide-react";
import { resolveInvoiceStatus } from "@/lib/statusResolvers";  // Phase STATE-DRIFT-SWEEP

const STATUS_LABEL = {
    sent: "Sent", viewed: "Viewed", paid: "Paid", overdue: "Overdue", void: "Void",
};
const STATUS_COLOR = {
    sent: "bg-blue-50 text-blue-900 ring-blue-200",
    viewed: "bg-violet-50 text-violet-800 ring-violet-200",
    paid: "bg-emerald-100 text-emerald-900 ring-emerald-200",
    overdue: "bg-red-50 text-red-700 ring-red-200",
    void: "bg-slate-200 text-slate-700 ring-slate-300",
};
const PAYABLE = new Set(["sent", "viewed", "overdue"]);

const POLL_INTERVAL_MS = 5000;
const POLL_MAX = 12; // 12 × 5s = 60s — bound on polling cycles
const STALL_TIMEOUT_MS = 60000; // absolute deadline for optimistic "processing"

function fmtMoney(cents) {
    if (cents == null) return "—";
    const sign = cents < 0 ? "-" : "";
    const v = Math.abs(cents) / 100;
    return `${sign}$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) { if (!s) return "—"; return s.slice(0, 10); }
function fmtDateTime(s) {
    if (!s) return "—";
    try {
        const d = new Date(s);
        return d.toLocaleString();
    } catch { return s; }
}

export default function CustomerInvoiceDetailPage() {
    const { id } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const [inv, setInv] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pdfUrl, setPdfUrl] = useState(null);

    // Pay flow state
    const [payOpen, setPayOpen] = useState(false);
    // Optimistic local "we just returned from Stripe" state. Drives the
    // amber processing card immediately so the user is never left looking
    // at an unchanged page while we wait for Stripe's webhook to arrive.
    //   "processing" → optimistic amber card (no server confirmation yet)
    //   "failed"     → red card with the message Stripe handed us in
    //                   ?redirect_status=failed
    //   "stalled"    → info card when 60s of polling elapsed with no
    //                   server-side payment_in_flight (probably Stripe
    //                   didn't receive a payment method or webhook hasn't
    //                   landed yet)
    //   null         → no return-from-Stripe overlay; trust server state.
    const [returnState, setReturnState] = useState(null);
    const [returnError, setReturnError] = useState(null);
    const [polling, setPolling] = useState(false);
    const pollCountRef = useRef(0);
    const pollTimerRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/customer/invoices/${id}`);
            setInv(data);
            return data;
        } catch (err) {
            toast.error(formatApiError(err));
            return null;
        } finally {
            setLoading(false);
        }
    }, [id]);

    const loadPreview = async () => {
        try {
            const res = await api.get(`/customer/invoices/${id}/pdf`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            setPdfUrl((prev) => {
                if (prev) URL.revokeObjectURL(prev);
                return url;
            });
        } catch {
            // best-effort — detail page still works without preview
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);
    useEffect(() => {
        if (inv) loadPreview();
        return () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); };
        /* eslint-disable-next-line */
    }, [inv?.id, inv?.status]);

    /**
     * Handle the query params after page mount:
     *   ?pay=1                                  → auto-open the pay modal (list page deep-link)
     *   ?pay_return=1                           → we set this on our return_url
     *   ?payment_intent / ?payment_intent_client_secret / ?redirect_status
     *                                           → appended by Stripe on redirect
     *
     * Regardless of WHICH of those are present, treat the page as a
     * Stripe return and immediately enter the optimistic processing
     * state. We then start polling for the real server-side payment_in_flight
     * to land (the webhook may take several seconds for ACH). All return
     * params are stripped via replaceState so a refresh/copy-paste doesn't
     * re-fire the optimistic flow.
     */
    useEffect(() => {
        if (!inv) return;
        const sp = new URLSearchParams(searchParams);
        let touched = false;

        // Any of these signals "Stripe handed control back to us".
        const isStripeReturn =
            sp.get("pay_return") === "1" ||
            !!sp.get("payment_intent") ||
            !!sp.get("payment_intent_client_secret") ||
            !!sp.get("redirect_status");

        if (isStripeReturn) {
            const redirectStatus = sp.get("redirect_status");
            if (redirectStatus === "failed") {
                setReturnState("failed");
                setReturnError(
                    "Your bank authorization didn't complete. Please try again or use a different bank.",
                );
            } else {
                // succeeded / processing / null all map to "optimistic
                // processing card". The actual settlement is gated on the
                // succeeded webhook — never on this redirect status.
                setReturnState("processing");
                setReturnError(null);
            }
            // Strip every Stripe-related param so refresh doesn't replay.
            ["pay_return", "payment_intent", "payment_intent_client_secret",
             "redirect_status", "source_redirect_slug"].forEach((k) => sp.delete(k));
            touched = true;
        }
        if (sp.get("pay") === "1") {
            if (PAYABLE.has(inv.status) && !inv.payment_in_flight) {
                setPayOpen(true);
            } else if (inv.payment_in_flight) {
                setReturnState("processing");
            }
            sp.delete("pay");
            touched = true;
        }
        if (touched) setSearchParams(sp, { replace: true });
        /* eslint-disable-next-line */
    }, [inv?.id]);

    // Polling: bounded to 60s. We poll the lightweight payment-status
    // endpoint, but refresh the full invoice when it transitions to a
    // terminal state so the page reflects status=paid, paid_at, etc.
    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        setPolling(false);
    }, []);

    const tickPoll = useCallback(async () => {
        try {
            const { data: status } = await api.get(`/customer/invoices/${id}/payment-status`);
            const s = status?.status;
            // Terminal: succeeded should also trigger a full reload so the
            // invoice status=paid + paid_at land in UI immediately.
            if (s === "succeeded") {
                setReturnState(null); // server confirms; drop optimistic state
                setReturnError(null);
                stopPolling();
                await load();
                return;
            }
            if (s === "failed" || s === "canceled") {
                // Server now has the failure detail in
                // last_payment_error_message. Surface it via the inline
                // failed card by clearing our optimistic state.
                setReturnState(null);
                setReturnError(null);
                stopPolling();
                await load();
                return;
            }
            // Refresh invoice (cheap) so payment_in_flight + error flip
            // even mid-poll if a webhook arrives.
            const fresh = await load();
            // If the server is now reporting in-flight, drop the optimistic
            // overlay and trust server state from here on.
            if (fresh && fresh.payment_in_flight) {
                setReturnState(null);
            }
        } catch {
            // Network blip — keep counting toward the cap.
        }
        pollCountRef.current += 1;
        if (pollCountRef.current >= POLL_MAX) {
            // 60s elapsed with no server-side confirmation. Downgrade the
            // optimistic processing card to a stalled-info card so the
            // user isn't lied to indefinitely. Do NOT mark the invoice as
            // anything — Stripe is still the source of truth.
            stopPolling();
            setReturnState((cur) => (cur === "processing" ? "stalled" : cur));
            return;
        }
        pollTimerRef.current = setTimeout(tickPoll, POLL_INTERVAL_MS);
    }, [id, load, stopPolling]);

    useEffect(() => {
        // Kick off polling whenever we have ANY reason to believe a payment
        // might be in motion: we just returned from Stripe (optimistic) OR
        // the server says it's in flight. Once status=paid, stop.
        if (!inv) return;
        if (inv.status === "paid") { stopPolling(); return; }
        const shouldPoll = returnState === "processing" || inv.payment_in_flight;
        if (shouldPoll && !polling) {
            pollCountRef.current = 0;
            setPolling(true);
            pollTimerRef.current = setTimeout(tickPoll, POLL_INTERVAL_MS);
        }
        return () => {
            if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
        };
        /* eslint-disable-next-line */
    }, [inv?.id, inv?.status, inv?.payment_in_flight, returnState]);

    /**
     * Absolute 60s deadline for the optimistic "processing" overlay.
     *
     * This runs INDEPENDENT of the polling loop's pollCount cap so it
     * cannot be defeated by:
     *   - The polling loop being prematurely stopped/restarted
     *   - A polling-effect re-run that resets pollCountRef
     *   - The payment-status endpoint returning {status: null} forever
     *     because no PI / payment row exists for this invoice yet
     *
     * If after 60s we're still in returnState === "processing" AND the
     * server has not flipped payment_in_flight to true, we downgrade to
     * "stalled" so the user gets the info card with refresh / contact
     * support. If payment_in_flight DID flip true, the polling tick
     * already cleared returnState to null and this timer is a no-op.
     */
    useEffect(() => {
        if (returnState !== "processing") return;
        const t = setTimeout(() => {
            setReturnState((cur) => (cur === "processing" ? "stalled" : cur));
        }, STALL_TIMEOUT_MS);
        return () => clearTimeout(t);
    }, [returnState]);

    const downloadPdf = async () => {
        try {
            const res = await api.get(`/customer/invoices/${id}/pdf?download=1`, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `${inv.invoice_number}.pdf`;
            a.click();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const openPayModal = () => {
        // Wipe any prior failure-card state when the user explicitly retries.
        setPayOpen(true);
    };

    const onModalInitiated = () => {
        // Stripe is about to redirect — close the modal so it doesn't flash
        // back on the post-return page mount.
        setPayOpen(false);
    };

    const lineItems = inv?.line_items || [];

    // Phase TOLLBILL-P5e (2026-07-10) — fetch the toll bill PDF via
    // authenticated axios so the JWT interceptor attaches. Then open
    // the resulting object URL in a new tab. 404 → friendly toast; the
    // page is unaffected.
    const openTollBillPdf = useCallback(async (billId) => {
        try {
            const resp = await api.get(
                `/customer/tolls/bills/${encodeURIComponent(billId)}/attachment`,
                { responseType: "blob" },
            );
            const blob = new Blob([resp.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 404) {
                toast.error("PDF not available for this bill");
            } else {
                toast.error(formatApiError(err));
            }
        }
    }, []);
    // Phase STATE-DRIFT-SWEEP — single resolver drives label, tone,
    // Pay-now gate, isPaid and overdue banner. Handles the previously
    // unmapped `partial_paid` status + cron-lag past-due drift window.
    const resolved = inv ? resolveInvoiceStatus(inv) : null;
    const isPaid = !!resolved?.isPaid;
    const inFlight = !!inv?.payment_in_flight;
    const lastError = inv?.last_payment_error_message || null;
    const showProcessingCard = !isPaid && (inFlight || returnState === "processing");
    const showStalledCard = !isPaid && !inFlight && returnState === "stalled";
    const showOptimisticFailed = !isPaid && returnState === "failed" && !inFlight;
    const showServerFailed = !isPaid && !inFlight && !returnState && lastError;
    const showPayButton = useMemo(
        () => resolved?.isPayable && !inFlight && !isPaid
            && returnState !== "processing"
            && returnState !== "stalled"
            && returnState !== "failed",
        [resolved, inFlight, isPaid, returnState],
    );

    if (loading) return <DashboardShell><p className="text-sm text-slate-500" data-testid="customer-invoice-loading">Loading…</p></DashboardShell>;
    if (!inv) return (
        <DashboardShell>
            <p className="text-sm text-slate-500">Invoice not found.</p>
            <Button asChild variant="ghost" size="sm" className="mt-3 gap-1.5">
                <Link to="/customer/invoices"><ArrowLeft size={14} /> Back to invoices</Link>
            </Button>
        </DashboardShell>
    );

    return (
        <DashboardShell>
            <div className="fade-up flex flex-wrap items-start justify-between gap-3">
                <div>
                    <Button asChild variant="ghost" size="sm" className="-ml-3 gap-1.5">
                        <Link to="/customer/invoices" data-testid="customer-invoice-back">
                            <ArrowLeft size={14} /> All invoices
                        </Link>
                    </Button>
                    <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-3"
                        data-testid="customer-invoice-number">
                        {inv.invoice_number}
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        Issued {fmtDate(inv.issue_date)} · Due {fmtDate(inv.due_date)}
                    </p>
                </div>
                <div className="text-right">
                    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ${resolved?.tone || "bg-slate-100 ring-slate-200"}`}
                          data-testid="customer-invoice-status">
                        {resolved?.label || "—"}
                    </span>
                    <p className="text-3xl font-display font-bold tracking-tight text-slate-900 mt-3 tabular-nums"
                       data-testid="customer-invoice-total">
                        {fmtMoney(inv.total_cents)}
                    </p>
                    {resolved?.isOverdue && (
                        <p className="text-xs text-red-700 inline-flex items-center gap-1 mt-1">
                            <AlertTriangle size={12} /> This invoice is past due
                        </p>
                    )}
                </div>
            </div>

            {/* Payment state cards */}
            {isPaid && (
                <Card
                    className="mt-6 border-emerald-200 bg-emerald-50/60"
                    data-testid="customer-invoice-paid-card"
                >
                    <CardContent className="p-4 flex items-start gap-3">
                        <CheckCircle2 className="text-emerald-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="font-semibold text-emerald-900">
                                Paid {fmtDateTime(inv.paid_at)}
                            </p>
                            <p className="text-sm text-emerald-800 mt-0.5">
                                Thanks — your payment of{" "}
                                <strong>{fmtMoney(inv.paid_amount_cents || inv.total_cents)}</strong>{" "}
                                cleared via bank transfer.
                                {inv.payment_reference_masked && (
                                    <> Reference <span className="font-mono">{inv.payment_reference_masked}</span>.</>
                                )}{" "}
                                A receipt was emailed to your billing contact.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {!isPaid && showProcessingCard && (
                <Card
                    className="mt-6 border-amber-200 bg-amber-50/60"
                    data-testid="customer-invoice-processing-card"
                >
                    <CardContent className="p-4 flex items-start gap-3">
                        <Clock className="text-amber-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="font-semibold text-amber-900 flex items-center gap-2">
                                Payment processing
                                {polling && <Loader2 size={12} className="animate-spin text-amber-700" />}
                            </p>
                            <p className="text-sm text-amber-800 mt-0.5">
                                {inFlight
                                    ? <>We received your authorization. Your bank typically takes
                                        3–5 business days to settle. We'll email you the moment it
                                        clears — you don't need to do anything else.</>
                                    : <>You returned from your bank successfully. We're waiting for
                                        Stripe to confirm — this usually takes a few seconds. Stay
                                        on this page; we'll update as soon as we hear back.</>}
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {showStalledCard && (
                <Card
                    className="mt-6 border-blue-200 bg-blue-50/60"
                    data-testid="customer-invoice-stalled-card"
                >
                    <CardContent className="p-4 flex items-start gap-3">
                        <AlertTriangle className="text-blue-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="font-semibold text-blue-900">
                                Still waiting on your bank
                            </p>
                            <p className="text-sm text-blue-800 mt-0.5">
                                We haven't received confirmation from Stripe yet. Your
                                payment may still be processing — refresh in a few
                                minutes, or contact support if you believe you were
                                charged.
                            </p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                <Button
                                    onClick={() => window.location.reload()}
                                    size="sm"
                                    variant="outline"
                                    className="gap-1.5 border-blue-300 text-blue-900 hover:bg-blue-100"
                                    data-testid="customer-invoice-stalled-refresh"
                                >
                                    <RotateCw size={13} /> Refresh
                                </Button>
                                <Button
                                    asChild
                                    size="sm"
                                    variant="ghost"
                                    className="gap-1.5 text-blue-900 hover:bg-blue-100"
                                    data-testid="customer-invoice-stalled-support"
                                >
                                    <a href="mailto:support@allabouttrucking.us?subject=Payment%20status%20check%20-%20Invoice">
                                        Contact support
                                    </a>
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {showOptimisticFailed && (
                <Card
                    className="mt-6 border-red-200 bg-red-50/60"
                    data-testid="customer-invoice-failed-card"
                >
                    <CardContent className="p-4 flex items-start gap-3">
                        <AlertTriangle className="text-red-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="font-semibold text-red-900">Bank authorization didn't complete</p>
                            <p className="text-sm text-red-800 mt-0.5">
                                {returnError || "Stripe returned a failed authorization. Please try again."}
                            </p>
                            <div className="mt-3">
                                <Button
                                    onClick={() => { setReturnState(null); setReturnError(null); openPayModal(); }}
                                    size="sm"
                                    className="bg-red-700 hover:bg-red-800 gap-1.5"
                                    data-testid="customer-invoice-try-again"
                                >
                                    <RotateCw size={13} /> Try again
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            {showServerFailed && (
                <Card
                    className="mt-6 border-red-200 bg-red-50/60"
                    data-testid="customer-invoice-failed-card"
                >
                    <CardContent className="p-4 flex items-start gap-3">
                        <AlertTriangle className="text-red-700 mt-0.5" size={20} />
                        <div className="flex-1">
                            <p className="font-semibold text-red-900">Your last payment didn't go through</p>
                            <p className="text-sm text-red-800 mt-0.5">
                                {lastError}
                            </p>
                            <div className="mt-3">
                                <Button
                                    onClick={openPayModal}
                                    size="sm"
                                    className="bg-red-700 hover:bg-red-800 gap-1.5"
                                    data-testid="customer-invoice-try-again"
                                >
                                    <RotateCw size={13} /> Try again
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            )}

            <div className="mt-6 flex flex-wrap gap-2" data-testid="customer-invoice-actions">
                <Button onClick={downloadPdf} variant="outline" className="gap-2"
                        data-testid="customer-invoice-download">
                    <Download size={14} /> Download PDF
                </Button>
                {showPayButton && (
                    <Button
                        onClick={openPayModal}
                        className="bg-blue-900 hover:bg-blue-800 gap-2"
                        data-testid="customer-invoice-pay-now"
                    >
                        <CreditCard size={14} /> Pay now
                    </Button>
                )}
                {!showPayButton && isPaid && (
                    <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700 px-3 py-1.5 rounded-md bg-emerald-50 ring-1 ring-emerald-200">
                        <Lock size={12} /> Paid {fmtDate(inv.paid_at)}
                        {inv.payment_reference_masked && <span className="font-mono text-emerald-800">· {inv.payment_reference_masked}</span>}
                    </span>
                )}
            </div>

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <FileText size={16} /> Invoice details
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Issue date</p>
                                <p className="text-slate-900 font-medium">{fmtDate(inv.issue_date)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Due date</p>
                                <p className="text-slate-900 font-medium">{fmtDate(inv.due_date)}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Terms</p>
                                <p className="text-slate-900 font-medium">{inv.terms}</p>
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wider text-slate-500">Amount</p>
                                <p className="text-slate-900 font-semibold tabular-nums">{fmtMoney(inv.total_cents)}</p>
                            </div>
                            {inv.customer_notes && (
                                <div className="col-span-2 mt-1 rounded-md bg-blue-50/50 border border-blue-100 p-3">
                                    <p className="text-xs uppercase tracking-wider text-blue-900 mb-1">Note from RentFlow</p>
                                    <p className="text-sm text-slate-700 whitespace-pre-line">{inv.customer_notes}</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    <CustomerInvoiceAttachments
                        invoiceId={inv.id}
                        attachments={inv.attachments}
                    />

                    <Card>
                        <CardHeader>
                            <CardTitle className="text-base font-semibold">Line items</CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            {/* Phase K.5.2 (2026-08-07) — mobile: stacked-card list. */}
                            <div className="sm:hidden divide-y divide-slate-100"
                                 data-testid="customer-invoice-lines-mobile">
                                {lineItems.length === 0 && (
                                    <div className="py-6 px-4 text-center text-sm text-slate-400 italic">
                                        No line items.
                                    </div>
                                )}
                                {lineItems.map((li) => {
                                    const tollBillId = (li.metadata && li.metadata.kind === "toll_base")
                                        ? li.metadata.bill_id
                                        : null;
                                    return (
                                        <div key={li.id}
                                             className="p-4"
                                             data-testid={`customer-invoice-line-${li.id}`}>
                                            <div className="text-sm text-slate-900 flex items-center gap-2 flex-wrap mb-1 break-words">
                                                <span>{li.description}</span>
                                                {tollBillId && (
                                                    <button
                                                        type="button"
                                                        onClick={() => openTollBillPdf(tollBillId)}
                                                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                                        data-testid={`customer-invoice-line-pdf-${li.id}`}
                                                        title={`View toll bill PDF (${tollBillId})`}
                                                    >
                                                        <FileText size={11} /> View PDF
                                                    </button>
                                                )}
                                            </div>
                                            <div className="text-xs text-slate-500 capitalize mb-2">
                                                {(li.line_type || "other").replace("_", " ")}
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-slate-600">
                                                <span>
                                                    Qty {li.quantity}
                                                    <span className="mx-1 text-slate-400">·</span>
                                                    {fmtMoney(li.unit_amount_cents)} each
                                                </span>
                                                <span className="text-sm font-semibold text-slate-900 tabular-nums">
                                                    {fmtMoney(li.total_cents)}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                                {lineItems.length > 0 && (
                                    <div className="p-4 bg-slate-50 flex items-center justify-between">
                                        <span className="text-sm font-semibold text-slate-500">Total</span>
                                        <span className="text-lg font-bold text-blue-900 tabular-nums">
                                            {fmtMoney(inv.total_cents)}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Desktop/tablet: full table (unchanged). */}
                            <table className="hidden sm:table min-w-full text-sm">
                                <thead className="text-xs text-slate-500 uppercase border-b border-slate-100">
                                    <tr>
                                        <th className="text-left py-2 px-4">Description</th>
                                        <th className="text-right py-2 px-4">Qty</th>
                                        <th className="text-right py-2 px-4">Unit</th>
                                        <th className="text-right py-2 px-4">Amount</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {lineItems.map((li) => {
                                        // Phase TOLLBILL-P5e — surface a "View PDF"
                                        // affordance next to the description on toll
                                        // base lines that carry a bill_id.
                                        const tollBillId = (li.metadata && li.metadata.kind === "toll_base")
                                            ? li.metadata.bill_id
                                            : null;
                                        return (
                                        <tr key={li.id} data-testid={`customer-invoice-line-${li.id}`}>
                                            <td className="py-2 px-4">
                                                <div className="text-slate-900 flex items-center gap-2 flex-wrap">
                                                    <span>{li.description}</span>
                                                    {tollBillId && (
                                                        <button
                                                            type="button"
                                                            onClick={() => openTollBillPdf(tollBillId)}
                                                            className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50 hover:border-slate-300 transition-colors"
                                                            data-testid={`customer-invoice-line-pdf-${li.id}`}
                                                            title={`View toll bill PDF (${tollBillId})`}
                                                        >
                                                            <FileText size={11} /> View PDF
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="text-xs text-slate-500 capitalize">{(li.line_type || "other").replace("_", " ")}</div>
                                            </td>
                                            <td className="py-2 px-4 text-right tabular-nums text-slate-700">{li.quantity}</td>
                                            <td className="py-2 px-4 text-right tabular-nums text-slate-700">{fmtMoney(li.unit_amount_cents)}</td>
                                            <td className="py-2 px-4 text-right tabular-nums font-semibold text-slate-900">{fmtMoney(li.total_cents)}</td>
                                        </tr>
                                        );
                                    })}
                                    {lineItems.length === 0 && (
                                        <tr><td colSpan={4} className="py-6 px-4 text-center text-sm text-slate-400 italic">No line items.</td></tr>
                                    )}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t border-slate-200">
                                        <td colSpan={3} className="py-3 px-4 text-right font-semibold text-slate-500">Total</td>
                                        <td className="py-3 px-4 text-right font-bold text-blue-900 tabular-nums text-lg">{fmtMoney(inv.total_cents)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </CardContent>
                    </Card>
                </div>

                <div className="lg:col-span-1">
                    <Card className="sticky top-4">
                        <CardHeader>
                            <CardTitle className="text-base font-semibold flex items-center gap-2">
                                <FileText size={16} /> PDF preview
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-2">
                            {pdfUrl ? (
                                <PdfPreview
                                    src={pdfUrl}
                                    downloadHref={pdfUrl}
                                    downloadName={inv?.invoice_number ? `${inv.invoice_number}.pdf` : "invoice.pdf"}
                                    testid="customer-invoice-pdf-preview"
                                />
                            ) : (
                                <p className="text-sm text-slate-500 p-6 text-center">Generating preview…</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* Phase K.4 (2026-08-07) — Credit memos (renders only when memos exist). */}
            <div className="max-w-5xl mx-auto px-4">
                <CreditMemosCard
                    memos={inv?.credit_memos}
                    paidCents={inv?.total_cents}
                    refundedCents={inv?.refunded_amount_cents}
                />
            </div>

            <InvoicePayModal
                invoice={inv}
                open={payOpen}
                onOpenChange={setPayOpen}
                onInitiated={onModalInitiated}
            />
        </DashboardShell>
    );
}
